import {makeStyles} from 'tss-react/mui';
import {ChangeEvent, Dispatch, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ApiButton,
  Checkbox,
  ErrorInfo,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableMeta,
  Progress,
  SectionHeader,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import {ANNOUNCEMENT_TYPES} from 'web-library/models/Announcements';
import {useGetAdminAnnouncementConfigs} from '../../../api-refactored/announcements-configuration/useGetAdminAnnouncementConfigs';
import {usePutAdminAnnouncementConfigs} from '../../../api-refactored/announcements-configuration/usePutAdminAnnouncementConfigs';
import {stringToSemanticDisplay} from '../../../utils/DateUtils';

const useStyles = makeStyles()((theme) =>
    ({
      updatedByText: {
        color: theme.color.text.textTertiary,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
      },
      marginBottom2: {
        marginBottom: theme.spacing(2),
      },
    }));

const CONFIGS = [
  {name: 'titleEnabled', label: 'Title', excludeFeatures: []},
  {name: 'descriptionEnabled', label: 'Description', excludeFeatures: []},
  {name: 'priorityEnabled', label: 'Set As Priority', excludeFeatures: []},
  {name: 'documentEnabled', label: 'Document Upload', excludeFeatures: []},
  {name: 'validityPeriodEnabled', label: 'Validity Period', excludeFeatures: []},
  {
    name: 'recipientSelectionEnabled',
    label: 'Recipient Selection',
    excludeFeatures: ['BROADCAST_MESSAGE', 'ADVISORY_BANNER', 'PAYMENT_MESSAGE'],
  },
];

const useTableColumns = (
    tableState: TableState,
    tableDispatch: Dispatch<TableActionTypes>
): MUIDataTableColumnDef[] => {
  const customBodyRender = (
      value: boolean,
      tableMeta: MUIDataTableMeta,
      excludeFeatures: string[]
  ) =>
      (!excludeFeatures.length || !excludeFeatures.includes(tableMeta.rowData[0])) && (
          <Checkbox
              checked={value}
              name={tableMeta.columnData.name}
              onChange={(event) => updateConfig(event, tableMeta.rowIndex)}
          />
      );

  const updateConfig = (event: ChangeEvent<HTMLInputElement>, dataIndex: number) => {
    const {
      target: {checked, name},
    } = event;
    const newConfig = [...tableState.data];

    newConfig[dataIndex] = {
      ...tableState.data[dataIndex],
      [name]: checked,
    };

    tableDispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: newConfig,
        count: newConfig.length,
      },
    } as TableActionTypes);
  };

  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'announcementType',
      label: 'Feature',
      options: {
        customBodyRender: (value: any) =>
            value &&
            Object.entries(ANNOUNCEMENT_TYPES)
            .filter(([k, _v]) => k === value)
            .map(([_k, v]) => v),
      },
    },
  ];

  return tableColumns.concat(
      CONFIGS.map(
          ({name, label, excludeFeatures}): MUIDataTableColumnDef => ({
            name,
            label,
            options: {
              customBodyRender: (value: any, tableMeta: MUIDataTableMeta) =>
                  customBodyRender(value, tableMeta, excludeFeatures),
            },
          })
      )
  );
};

const AdminAnnouncementsConfigs = () => {
  const {classes} = useStyles();
  const {t} = useTranslation();

  const [updatedBy, setUpdatedBy] = useState('-');
  const [updatedDateTime, setUpdatedDateTime] = useState('-');

  const {state: tableState, dispatch: tableDispatch} = useDataTable();
  const {snackbarHandler} = useSnackbarHandler();

  const {
    data: announcementConfigsData,
    isLoading: isAnnouncementConfigsLoading,
    refetch: getAnnouncementConfigs,
    error: getAnnouncementConfigsError,
  } = useGetAdminAnnouncementConfigs({
    onSuccess: ({announcementConfigs, lastUpdatedBy, lastUpdatedDateTime}) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: announcementConfigs,
          count: announcementConfigs.length,
        },
      } as TableActionTypes);

      if (lastUpdatedBy?.length && lastUpdatedDateTime?.length) {
        setUpdatedBy(lastUpdatedBy);
        setUpdatedDateTime(stringToSemanticDisplay(lastUpdatedDateTime));
      }
    },
  });

  const {
    mutate: putAnnouncementConfigs,
    status: putAnnouncementConfigsStatus,
    error: putAnnouncementConfigsError,
  } = usePutAdminAnnouncementConfigs({
    onSuccess: () => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>AnnouncementsConfiguration.SuccessMsg'),
      });
      getAnnouncementConfigs();
    },
    onError: () =>
        snackbarHandler?.setGlobalSnackbar({
          targetSeverity: 'error',
          targetData: t('admin->>AnnouncementsConfiguration.ErrorMsg'),
          targetAnchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        }),
  });

  const tableColumns = useTableColumns(tableState, tableDispatch);

  if (isAnnouncementConfigsLoading) {
    return <Progress color="secondary"/>;
  }

  return (
      <>
        <SectionHeader title={t('admin->>AnnouncementsConfiguration.Header')}/>

        <MUIDataTable
            state={tableState}
            dispatch={tableDispatch}
            columns={tableColumns}
            data={tableState.data}
        />

        <Typography variant="subtitle2" className={classes.updatedByText}>
          {`Last updated by ${updatedBy} on ${updatedDateTime}`}
        </Typography>

        {getAnnouncementConfigsError && (
            <ErrorInfo
                className={classes.marginBottom2}
                label={getAnnouncementConfigsError.response?.data.message || ''}
            />
        )}
        {putAnnouncementConfigsError && (
            <ErrorInfo
                className={classes.marginBottom2}
                label={putAnnouncementConfigsError.response?.data.message || ''}
            />
        )}
        <ApiButton
            id="announcements-configure-update"
            status={putAnnouncementConfigsStatus}
            onClick={() => putAnnouncementConfigs({announcementConfigs: tableState.data})}
            color="primary"
            disabled={
              JSON.stringify(tableState.data) ===
              JSON.stringify(announcementConfigsData?.announcementConfigs)
            }
        >
          {t('admin->>AnnouncementsConfiguration.Update')}
        </ApiButton>
      </>
  );
};

export default AdminAnnouncementsConfigs;
